/**
 * Custom TipTap extension for Tab/Shift-Tab paragraph and heading indentation.
 *
 * Adds an `indent` attribute (0–8) to paragraph and heading nodes.
 * Tab increases indent; Shift-Tab decreases it. Skips when inside
 * lists (which already handle Tab for nesting).
 *
 * Closes #181
 */

import { Extension } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const MAX_INDENT = 8;

export const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const value = element.getAttribute('data-indent');
              return value ? parseInt(value, 10) : 0;
            },
            renderHTML: (attributes) => {
              if (!attributes.indent || attributes.indent === 0) return {};
              return { 'data-indent': attributes.indent };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state }) => {
          const { selection } = state;
          let changed = false;

          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
              const current = (node.attrs.indent as number) || 0;
              if (current < MAX_INDENT) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: current + 1,
                });
                changed = true;
              }
            }
          });

          return changed;
        },

      outdent:
        () =>
        ({ tr, state }) => {
          const { selection } = state;
          let changed = false;

          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
              const current = (node.attrs.indent as number) || 0;
              if (current > 0) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: current - 1,
                });
                changed = true;
              }
            }
          });

          return changed;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // Don't handle Tab when inside a list — let the list extension handle it
        const { $from } = this.editor.state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === 'listItem' || node.type.name === 'taskItem') {
            return false;
          }
        }
        return this.editor.commands.indent();
      },
      'Shift-Tab': () => {
        const { $from } = this.editor.state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === 'listItem' || node.type.name === 'taskItem') {
            return false;
          }
        }
        return this.editor.commands.outdent();
      },
    };
  },
});
